import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import VColorPickerField from "@/components/forms/VColorPickerField.vue";
import { useTranslations } from "@/hooks/useTranslations";
import VIngredientsDisplayNames from "@/components/vIngredientsDisplayNames.vue";
import VUSContainsPercentOfForm from "@/components/vUSContainsPercentOfForm.vue";
import VToggleSwitch from "@/components/vToggleSwitch.vue";
import {Recipe} from "@/models/Recipes/Recipe";
import IngredientDisplay, {
  type IngredientAllergenFormattingFontFamily,
  type IngredientAllergenFormattingFontSize,
  type IngredientAllergenFormattingStyle,
} from "@/models/Ingredient/IngredientDisplay";
import {LabelIngredientConfiguration} from "@/models/NutritionLabels/v2/LabelIngredientConfiguration";


export default /*#__PURE__*/_defineComponent({
  __name: 'IngredientConfiguration',
  props: {
    model: null,
    language: null,
    recipe: null,
    ingredientAndSubRecipeDisplays: null,
    subRecipeDisplays: null,
    getChangedDisplayNames: null,
    allergenFormattingStyle: null,
    allergenFormattingColor: null,
    allergenFormattingHighlightColor: null,
    allergenFormattingFontFamily: null,
    allergenFormattingFontSize: null,
    showAiDetector: { type: Boolean },
    aiAnalysisApplied: { type: Boolean },
    isApplyingAiAllergens: { type: Boolean },
    isTrialUser: { type: Boolean }
  },
  emits: [
  "fetch-preview",
  "toggle-subrecipes",
  "reset-ingredient-display",
  "reset-subrecipe-display",
  "ingredient-allergen-change",
  "update:model",
  "update-allergen-formatting-style",
  "update-allergen-formatting-color",
  "update-allergen-formatting-highlight-color",
  "update-allergen-formatting-font-family",
  "update-allergen-formatting-font-size",
  "apply-ai-allergens",
],
  setup(__props: any, { emit }) {

const props = __props as {
  model: LabelIngredientConfiguration;
  language: string;
  recipe: Recipe;
  ingredientAndSubRecipeDisplays: IngredientDisplay[];
  subRecipeDisplays: IngredientDisplay[];
  getChangedDisplayNames: any;
  allergenFormattingStyle?: IngredientAllergenFormattingStyle;
  allergenFormattingColor?: string;
  allergenFormattingHighlightColor?: string;
  allergenFormattingFontFamily?: IngredientAllergenFormattingFontFamily;
  allergenFormattingFontSize?: IngredientAllergenFormattingFontSize;
  showAiDetector?: boolean;
  aiAnalysisApplied?: boolean;
  isApplyingAiAllergens?: boolean;
  isTrialUser?: boolean;
};

const defaultAllergenFormattingColor = "#C62828";
const defaultAllergenHighlightColor = "#FFF176";
const defaultAllergenFontFamily: IngredientAllergenFormattingFontFamily = "Arial";
const defaultAllergenFontSize: IngredientAllergenFormattingFontSize = "7pt";
const allergenFontColorOptions = [
  "#000000",
  "#434343",
  "#666666",
  "#8D6E63",
  "#C62828",
  "#E67C00",
  "#F9A825",
  "#2E7D32",
  "#00838F",
  "#1565C0",
  "#283593",
  "#6A1B9A",
];
const allergenHighlightColorOptions = [
  "#FFF176",
  "#FFE082",
  "#FFCC80",
  "#FFAB91",
  "#F8BBD0",
  "#E1BEE7",
  "#D1C4E9",
  "#BBDEFB",
  "#B2EBF2",
  "#C8E6C9",
  "#DCEDC8",
  "#F0F4C3",
];
const allergenFontFamilyOptions: IngredientAllergenFormattingFontFamily[] = [
  "Arial",
  "Calibri",
  "Sans-Serif",
  "Tahoma",
  "Verdana",
];
const allergenFontFamilySelectOptions = allergenFontFamilyOptions.map((fontFamily) => ({
  id: fontFamily,
  name: fontFamily,
}));
const allergenFontSizeOptions: IngredientAllergenFormattingFontSize[] = ["5pt", "6pt", "7pt", "8pt", "9pt", "10pt"];
const allergenFontSizeSelectOptions = allergenFontSizeOptions.map((fontSize) => ({
  id: fontSize,
  name: fontSize,
}));





const translations = useTranslations();
const localModel = ref({ ...props.model });
const allergenFormattingOptions: Array<{
  value: IngredientAllergenFormattingStyle;
  title: string;
  icon?: string;
  shortLabel?: string;
  textClass?: string;
}> = [
  { value: "bold", title: "Bold", icon: "fa-bold" },
  { value: "italic", title: "Italic", icon: "fa-italic" },
  { value: "color", title: "Font colour" },
  { value: "uppercase", title: "Uppercase", shortLabel: "AA", textClass: "is-uppercase" },
  { value: "underline", title: "Underline", icon: "fa-underline" },
  { value: "font-family", title: "Font family", icon: "fa-font" },
  { value: "font-size", title: "Font size", icon: "fa-text-height" },
  { value: "background-color", title: "Highlight" },
];
const popupFormattingStyles: IngredientAllergenFormattingStyle[] = ["color", "background-color", "font-family", "font-size"];
const formattingControlsRef = ref<HTMLElement | null>(null);
const fontFamilySelect = ref<any | null>(null);
const fontSizeSelect = ref<any | null>(null);
const activeFormattingPopupStyle = ref<IngredientAllergenFormattingStyle | null>(null);
const hasAiAnalysisAppliedState = computed(() => Boolean(!props.isTrialUser && (
  props.aiAnalysisApplied
  || props.ingredientAndSubRecipeDisplays?.some((ingredient) =>
    ingredient.allergenVisualState && ingredient.allergenVisualState !== "default"
  )
)));
const selectedAllergenFormattingStyle = computed<IngredientAllergenFormattingStyle>(() =>
  props.allergenFormattingStyle || "bold"
);
const selectedAllergenFormattingColor = computed<string>(() =>
  props.allergenFormattingColor || defaultAllergenFormattingColor
);
const selectedAllergenFormattingHighlightColor = computed<string>(() =>
  props.allergenFormattingHighlightColor || defaultAllergenHighlightColor
);
const selectedAllergenFormattingFontFamily = computed<IngredientAllergenFormattingFontFamily>(() =>
  props.allergenFormattingFontFamily || defaultAllergenFontFamily
);
const selectedAllergenFormattingFontFamilyOption = computed(() =>
  allergenFontFamilySelectOptions.find((option) => option.id === selectedAllergenFormattingFontFamily.value)
  || allergenFontFamilySelectOptions[0]
);
const selectedAllergenFormattingFontSize = computed<IngredientAllergenFormattingFontSize>(() =>
  props.allergenFormattingFontSize || defaultAllergenFontSize
);
const selectedAllergenFormattingFontSizeOption = computed(() =>
  allergenFontSizeSelectOptions.find((option) => option.id === selectedAllergenFormattingFontSize.value)
  || allergenFontSizeSelectOptions[0]
);
const showFontColorPicker = computed(() => activeFormattingPopupStyle.value === "color");
const showHighlightColorPicker = computed(() => activeFormattingPopupStyle.value === "background-color");
const showFontFamilySelector = computed(() => activeFormattingPopupStyle.value === "font-family");
const showFontSizeSelector = computed(() => activeFormattingPopupStyle.value === "font-size");
const showActiveFormattingControl = computed(() => activeFormattingPopupStyle.value !== null);
const activeFormattingControlTitle = computed(() => {
  switch (activeFormattingPopupStyle.value) {
  case "color":
    return "Font colour";
  case "background-color":
    return "Highlight colour";
  case "font-family":
    return "Font family";
  case "font-size":
    return "Font size";
  default:
    return "Formatting options";
  }
});
const activeFormattingControlDescription = computed(() => {
  switch (activeFormattingPopupStyle.value) {
  case "color":
    return "Choose a text colour from the preset swatches.";
  case "background-color":
    return "Choose the highlight shade used behind matched allergen text.";
  case "font-family":
    return "Apply one approved font family to matched allergen text only.";
  case "font-size":
    return "Choose a size between 5 pt and 10 pt for matched allergen text.";
  default:
    return "Adjust the active allergen formatting style.";
  }
});
const showAiAllergenPrompt = computed(() =>
  Boolean(props.showAiDetector)
);
const showUkAllergenFormattingToolbar = computed(() =>
  Boolean(props.recipe?.nutritionCountry?.name === "UK" && localModel.value.includeIngredients)
);
const aiPromptStateClass = computed(() =>
  hasAiAnalysisAppliedState.value
    ? "allergen-ai-detector-prompt--clear"
    : ""
);
const aiPromptMascotState = computed(() => {
  if (props.isApplyingAiAllergens) {
    return "analyzing";
  }

  if (hasAiAnalysisAppliedState.value) {
    return "ready";
  }

  return "idle";
});
const aiPromptTitle = computed(() =>
  hasAiAnalysisAppliedState.value
    ? "AI ALLERGEN RESULTS APPLIED"
    : "Apply AI Allergens"
);
const aiPromptDescription = computed(() => {
  if (hasAiAnalysisAppliedState.value) {
    return "AI allergens are already in place. If you need to run them again, go back to the recipe allergen section.";
  }

  return "To apply AI allergens, go back to the recipe allergen section.";
});

function closeFormattingPopup() {
  activeFormattingPopupStyle.value = null;
}

function isPopupFormattingStyle(style: IngredientAllergenFormattingStyle) {
  return popupFormattingStyles.includes(style);
}

function handleOutsideFormattingPopupClick(event: MouseEvent) {
  const target = event.target as Node | null;

  if (!activeFormattingPopupStyle.value || !formattingControlsRef.value || !target) {
    return;
  }

  if (!formattingControlsRef.value.contains(target)) {
    closeFormattingPopup();
  }
}

function handleFormattingPopupEscape(event: KeyboardEvent) {
  if (event.key === "Escape" && activeFormattingPopupStyle.value) {
    closeFormattingPopup();
  }
}

onMounted(() => {
  document.addEventListener("mousedown", handleOutsideFormattingPopupClick);
  document.addEventListener("keydown", handleFormattingPopupEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleOutsideFormattingPopupClick);
  document.removeEventListener("keydown", handleFormattingPopupEscape);
});

// Emit updates
watch(
  localModel,
  (val) => {
    emit("update:model", val);
  },
  { deep: true }
);

// Emit events for actions
function emitFetchPreview() {
  emit("fetch-preview");
}

function emitToggleSubrecipes() {
  emit("toggle-subrecipes");
}

function emitResetIngredientDisplay() {
  emit("reset-ingredient-display");
}

function emitResetSubrecipeDisplay() {
  emit("reset-subrecipe-display");
}

function emitIngredientAllergenChange(payload: {
  ingredientId?: number;
  recipeId?: number;
  isAllergen: boolean;
  allergenVisualState: IngredientDisplay["allergenVisualState"];
}) {
  emit("ingredient-allergen-change", payload);
}

function emitAllergenFormattingStyle(style: IngredientAllergenFormattingStyle) {
  emit("update-allergen-formatting-style", style);
}

function emitAllergenFormattingColor(color: string) {
  emit("update-allergen-formatting-color", color);
}

function emitAllergenFormattingHighlightColor(color: string) {
  emit("update-allergen-formatting-highlight-color", color);
}

function emitAllergenFormattingFontFamily(fontFamily: IngredientAllergenFormattingFontFamily) {
  emit("update-allergen-formatting-font-family", fontFamily);
}

function emitAllergenFormattingFontSize(fontSize: IngredientAllergenFormattingFontSize) {
  emit("update-allergen-formatting-font-size", fontSize);
}

function handleAllergenFormattingStyleSelection(style: IngredientAllergenFormattingStyle) {
  emitAllergenFormattingStyle(style);

  if (style === "color" && !props.allergenFormattingColor) {
    emitAllergenFormattingColor(defaultAllergenFormattingColor);
  }

  if (style === "background-color" && !props.allergenFormattingHighlightColor) {
    emitAllergenFormattingHighlightColor(defaultAllergenHighlightColor);
  }

  if (style === "font-family" && !props.allergenFormattingFontFamily) {
    emitAllergenFormattingFontFamily(defaultAllergenFontFamily);
  }

  if (style === "font-size" && !props.allergenFormattingFontSize) {
    emitAllergenFormattingFontSize(defaultAllergenFontSize);
  }
}

async function handleAllergenFormattingButtonClick(style: IngredientAllergenFormattingStyle) {
  handleAllergenFormattingStyleSelection(style);

  if (!isPopupFormattingStyle(style)) {
    closeFormattingPopup();
    return;
  }

  activeFormattingPopupStyle.value = activeFormattingPopupStyle.value === style
    ? null
    : style;

  if (!activeFormattingPopupStyle.value) {
    return;
  }

  await nextTick();

  if (style === "font-family") {
    fontFamilySelect.value?.$el?.querySelector(".vs__dropdown-toggle")?.focus?.();
    return;
  }

  if (style === "font-size") {
    fontSizeSelect.value?.$el?.querySelector(".vs__dropdown-toggle")?.focus?.();
  }
}

function selectAllergenFormattingColor(color: string) {
  emitAllergenFormattingColor(color);

  if (selectedAllergenFormattingStyle.value !== "color") {
    emitAllergenFormattingStyle("color");
  }

  closeFormattingPopup();
}

function selectAllergenFormattingHighlightColor(color: string) {
  emitAllergenFormattingHighlightColor(color);

  if (selectedAllergenFormattingStyle.value !== "background-color") {
    emitAllergenFormattingStyle("background-color");
  }

  closeFormattingPopup();
}

function selectAllergenFormattingFontFamily(option: { id: IngredientAllergenFormattingFontFamily; name: string } | null) {
  if (!option) {
    return;
  }

  const fontFamily = option.id as IngredientAllergenFormattingFontFamily;
  emitAllergenFormattingFontFamily(fontFamily);

  if (selectedAllergenFormattingStyle.value !== "font-family") {
    emitAllergenFormattingStyle("font-family");
  }

  closeFormattingPopup();
}

function selectAllergenFormattingFontSize(option: { id: IngredientAllergenFormattingFontSize; name: string } | null) {
  if (!option) {
    return;
  }

  const fontSize = option.id as IngredientAllergenFormattingFontSize;
  emitAllergenFormattingFontSize(fontSize);

  if (selectedAllergenFormattingStyle.value !== "font-size") {
    emitAllergenFormattingStyle("font-size");
  }

  closeFormattingPopup();
}

return { __sfc: true,defaultAllergenFormattingColor, defaultAllergenHighlightColor, defaultAllergenFontFamily, defaultAllergenFontSize, allergenFontColorOptions, allergenHighlightColorOptions, allergenFontFamilyOptions, allergenFontFamilySelectOptions, allergenFontSizeOptions, allergenFontSizeSelectOptions, props, emit, translations, localModel, allergenFormattingOptions, popupFormattingStyles, formattingControlsRef, fontFamilySelect, fontSizeSelect, activeFormattingPopupStyle, hasAiAnalysisAppliedState, selectedAllergenFormattingStyle, selectedAllergenFormattingColor, selectedAllergenFormattingHighlightColor, selectedAllergenFormattingFontFamily, selectedAllergenFormattingFontFamilyOption, selectedAllergenFormattingFontSize, selectedAllergenFormattingFontSizeOption, showFontColorPicker, showHighlightColorPicker, showFontFamilySelector, showFontSizeSelector, showActiveFormattingControl, activeFormattingControlTitle, activeFormattingControlDescription, showAiAllergenPrompt, showUkAllergenFormattingToolbar, aiPromptStateClass, aiPromptMascotState, aiPromptTitle, aiPromptDescription, closeFormattingPopup, isPopupFormattingStyle, handleOutsideFormattingPopupClick, handleFormattingPopupEscape, emitFetchPreview, emitToggleSubrecipes, emitResetIngredientDisplay, emitResetSubrecipeDisplay, emitIngredientAllergenChange, emitAllergenFormattingStyle, emitAllergenFormattingColor, emitAllergenFormattingHighlightColor, emitAllergenFormattingFontFamily, emitAllergenFormattingFontSize, handleAllergenFormattingStyleSelection, handleAllergenFormattingButtonClick, selectAllergenFormattingColor, selectAllergenFormattingHighlightColor, selectAllergenFormattingFontFamily, selectAllergenFormattingFontSize, CollapsableCard, VColorPickerField, VIngredientsDisplayNames, VUSContainsPercentOfForm, VToggleSwitch }
}

})